import { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  FirestoreError
} from 'firebase/firestore';
import { Workstream, WorkstreamFormData, Milestone } from '../../components/workspace/types';
import { firestore } from '../config';

export const useWorkstreams = (workspaceId: string) => {
  const [workstreams, setWorkstreams] = useState<Workstream[]>([]);
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  
  useEffect(() => {
    const workstreamsRef = collection(firestore, `workspaces/${workspaceId}/workstreams`);
    
    const unsubscribe = onSnapshot(
      workstreamsRef,
      (snapshot) => {
        const workstreamsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Workstream[];
        
        setWorkstreams(workstreamsData);
        setStatus('success');
      },
      (error: FirestoreError) => {
        console.error('Workstreams error:', error);
        setStatus('error');
      }
    );

    return () => unsubscribe();
  }, [workspaceId]);

  const addWorkstream = async (data: WorkstreamFormData): Promise<void> => {
    const workstreamsRef = collection(firestore, `workspaces/${workspaceId}/workstreams`);
    const workstreamData = {
      ...data,
      startDate: data.startDate.toISOString(),
      endDate: data.endDate.toISOString()
    };
    await addDoc(workstreamsRef, workstreamData);
  };

  const updateWorkstream = async (id: string, data: Partial<WorkstreamFormData>): Promise<void> => {
    const workstreamRef = doc(firestore, `workspaces/${workspaceId}/workstreams/${id}`);
    const updateData = {
      ...data,
      startDate: data.startDate?.toISOString(),
      endDate: data.endDate?.toISOString()
    };
    await updateDoc(workstreamRef, updateData);
  };

  const deleteWorkstream = async (id: string): Promise<void> => {
    const workstreamRef = doc(firestore, `workspaces/${workspaceId}/workstreams/${id}`);
    await deleteDoc(workstreamRef);
  };

  const updateWorkstreamMilestones = async (workstreamId: string, milestones: Milestone[]): Promise<void> => {
    const workstreamRef = doc(firestore, `workspaces/${workspaceId}/workstreams/${workstreamId}`);
    await updateDoc(workstreamRef, {
      milestones: milestones.map(m => ({
        ...m,
        date: m.date instanceof Date ? m.date.toISOString() : m.date
      }))
    });
  };

  return {
    workstreams,
    status,
    addWorkstream,
    updateWorkstream,
    deleteWorkstream,
    updateWorkstreamMilestones
  };
};